const cancelado = () => new DOMException('Preparación cancelada', 'AbortError');
const siguienteFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

async function esperarImagen(img, signal) {
  if (signal?.aborted) throw cancelado();
  if (!img) throw new Error('Falta el arte de la invitación.');
  if (img.complete) {
    if (!img.naturalWidth) throw new Error('No se pudo cargar el arte de la invitación.');
    return;
  }
  await new Promise((resolve, reject) => {
    const limpiar = () => {
      clearTimeout(timer);
      img.removeEventListener('load', listo);
      img.removeEventListener('error', fallo);
      signal?.removeEventListener('abort', abortar);
    };
    const listo = () => { limpiar(); resolve(); };
    const fallo = () => { limpiar(); reject(new Error('No se pudo cargar el arte de la invitación.')); };
    const abortar = () => { limpiar(); reject(cancelado()); };
    const timer = setTimeout(fallo, 20000);
    img.addEventListener('load', listo);
    img.addEventListener('error', fallo);
    signal?.addEventListener('abort', abortar, { once: true });
  });
}

// Lee posiciones reales del render maestro. No clona documentos ni recarga el arte.
function rectLocal(node, origin) {
  const rect = node.getBoundingClientRect();
  return { x: rect.left - origin.left, y: rect.top - origin.top, width: rect.width, height: rect.height };
}

function dibujarNombre(ctx, node, origin) {
  const style = getComputedStyle(node);
  const size = parseFloat(style.fontSize);
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${size}px ${style.fontFamily}`;
  ctx.fillStyle = style.color;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  const shadow = style.textShadow;
  if (shadow && shadow !== 'none') {
    const color = shadow.match(/rgba?\([^)]+\)|#[a-f0-9]+/i);
    const lengths = shadow.replace(color?.[0] || '', '').match(/-?[\d.]+px/g) || [];
    ctx.shadowColor = color?.[0] || 'transparent';
    [ctx.shadowOffsetX, ctx.shadowOffsetY, ctx.shadowBlur] = lengths.map(value => parseFloat(value) * 4);
  }
  // Range conserva centrado y saltos de línea del navegador, también con nombres largos.
  const text = node.firstChild;
  if (!text || text.nodeType !== Node.TEXT_NODE) throw new Error('No se pudo leer el nombre de la invitación.');
  const value = text.textContent;
  const range = document.createRange();
  const metrics = ctx.measureText('Mg');
  const ascent = metrics.fontBoundingBoxAscent ?? size * .9;
  const descent = metrics.fontBoundingBoxDescent ?? size * .22;
  for (const match of value.matchAll(/\S+/g)) {
    range.setStart(text, match.index);
    range.setEnd(text, match.index + match[0].length);
    const rects = [...range.getClientRects()];
    if (rects.length !== 1) {
      // Una palabra que se parte por CSS se dibuja carácter por carácter.
      for (let i = 0; i < match[0].length; i++) {
        range.setStart(text, match.index + i); range.setEnd(text, match.index + i + 1);
        const r = range.getBoundingClientRect();
        ctx.fillText(match[0][i], r.left - origin.left, r.top - origin.top + (r.height - ascent - descent) / 2 + ascent);
      }
    } else {
      const r = rects[0];
      ctx.fillText(match[0], r.left - origin.left, r.top - origin.top + (r.height - ascent - descent) / 2 + ascent);
    }
  }
  range.detach();
}

export async function prepararInvitaciones(items, nodes, { signal, onProgress = () => {} } = {}) {
  if (!items.length || items.length !== nodes.length) throw new Error('Faltan invitaciones por preparar.');
  if (signal?.aborted) throw cancelado();
  const renders = nodes.map(node => node.querySelector('.palma-invitacion-render'));
  if (renders.some(node => !node)) throw new Error('Falta el render de una invitación.');
  await Promise.all(renders.map(node => esperarImagen(node.querySelector('.palma-invitacion-bg'), signal)));
  await document.fonts?.ready;
  await siguienteFrame(); await siguienteFrame();
  const base = document.createElement('canvas');
  const canvas = document.createElement('canvas');
  let baseKey = '';
  try {
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (signal?.aborted) throw cancelado();
      const source = renders[i], origin = source.getBoundingClientRect();
      if (!origin.width || !origin.height) throw new Error('La vista previa no tiene dimensiones.');
      const img = source.querySelector('.palma-invitacion-bg');
      const qr = source.querySelector('.palma-invitacion-qr');
      const qrCanvas = qr?.querySelector('canvas');
      const name = source.querySelector('.palma-invitacion-name');
      if (!qrCanvas || !name) throw new Error('Falta el QR o el nombre de una invitación.');
      const width = Math.round(origin.width * 4), height = Math.round(origin.height * 4);
      const key = `${img.currentSrc || img.src}|${width}|${height}`;
      if (key !== baseKey) {
        base.width = width; base.height = height;
        const bg = base.getContext('2d');
        bg.scale(4, 4); bg.fillStyle = '#fff'; bg.fillRect(0, 0, origin.width, origin.height);
        const r = rectLocal(img, origin);
        const factor = Math.max(r.width / img.naturalWidth, r.height / img.naturalHeight);
        const w = img.naturalWidth * factor, h = img.naturalHeight * factor;
        bg.drawImage(img, r.x + (r.width - w) / 2, r.y + (r.height - h) / 2, w, h);
        baseKey = key;
      }
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(base, 0, 0); ctx.scale(4, 4);
      const r = rectLocal(qr, origin), qrRect = rectLocal(qrCanvas, origin);
      ctx.fillStyle = getComputedStyle(qr).backgroundColor;
      const radius = parseFloat(getComputedStyle(qr).borderRadius) || 0;
      ctx.beginPath(); ctx.roundRect(r.x, r.y, r.width, r.height, radius); ctx.fill();
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(qrCanvas, qrRect.x, qrRect.y, qrRect.width, qrRect.height);
      ctx.save(); dibujarNombre(ctx, name, origin); ctx.restore();
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('No se pudo preparar una invitación.');
      if (signal?.aborted) throw cancelado();
      files.push(new File([blob], `invitacion-${items[i].id}.png`, { type: 'image/png' }));
      onProgress(i + 1);
      // Cede el hilo entre imágenes sin depender del RAF de un iframe oculto en iOS.
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    return files;
  } finally { base.width = base.height = canvas.width = canvas.height = 0; }
}
